import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { devWarn } from "@/lib/log";
import { ArrowUpRight, ArrowDownRight, Minus, TrendingUp, TrendingDown, IndianRupee } from "lucide-react";

/**
 * CashflowWidget — dashboard summary comparing this month's revenue
 * (delivered orders) against supplier payments due (POs sent/received).
 * Shows Received, Due, Net position with directional arrow.
 */
export default function CashflowWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/cashflow");
        setData(d);
      } catch (e) {
        devWarn("cashflow load failed", e);
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || error || !data) return null;

  const dir = data.direction;
  const netAccent =
    dir === "up" ? "text-emerald-600" : dir === "down" ? "text-[#FF3B30]" : "text-[#0D0D0D]";
  const NetIcon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  const netLabel =
    dir === "up" ? "Surplus" : dir === "down" ? "Deficit" : "Break-even";

  return (
    <div
      data-testid="cashflow-widget"
      className="mb-6 border border-[#E2E2E2] bg-white dark:bg-neutral-900 dark:border-neutral-800"
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#E2E2E2] dark:border-neutral-800">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C]">
          / cashflow · {data.month}
        </div>
        <Link
          to="/purchase-orders"
          data-testid="cashflow-view-pos"
          className="text-[10px] font-bold uppercase tracking-widest text-[#FF3B30] hover:underline flex items-center gap-1"
        >
          POs <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-3 divide-x divide-[#E2E2E2] dark:divide-neutral-800">
        {/* Received */}
        <div className="p-4 lg:p-5" data-testid="cashflow-received">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C5C5C] mb-2 flex items-center gap-1.5">
            <ArrowDownRight className="h-3 w-3 text-emerald-600" />
            Received
          </div>
          <div className="text-2xl lg:text-3xl font-black tracking-tight text-emerald-700 dark:text-emerald-400 flex items-center">
            <IndianRupee className="h-5 w-5" />
            <span data-testid="cashflow-received-value">{formatAmount(data.received)}</span>
          </div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5C5C5C]">
            {data.received_count} delivered
          </div>
        </div>

        {/* Due */}
        <div className="p-4 lg:p-5" data-testid="cashflow-due">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C5C5C] mb-2 flex items-center gap-1.5">
            <ArrowUpRight className="h-3 w-3 text-[#FF3B30]" />
            Payments due
          </div>
          <div className="text-2xl lg:text-3xl font-black tracking-tight text-[#FF3B30] flex items-center">
            <IndianRupee className="h-5 w-5" />
            <span data-testid="cashflow-due-value">{formatAmount(data.due)}</span>
          </div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5C5C5C]">
            {data.due_count} PO{data.due_count === 1 ? "" : "s"}
          </div>
        </div>

        {/* Net */}
        <div className="p-4 lg:p-5" data-testid="cashflow-net">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C5C5C] mb-2 flex items-center gap-1.5">
            <NetIcon className={`h-3 w-3 ${netAccent}`} />
            Net position
          </div>
          <div className={`text-2xl lg:text-3xl font-black tracking-tight ${netAccent} flex items-center`}>
            <IndianRupee className="h-5 w-5" />
            <span data-testid="cashflow-net-value">
              {formatAmount(Math.abs(data.net))}
            </span>
          </div>
          <div className={`mt-1 text-[10px] font-mono uppercase tracking-widest ${netAccent}`}>
            {netLabel}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatAmount(n) {
  const v = Number(n || 0);
  if (v >= 1_00_000) return (v / 1_00_000).toFixed(2) + "L";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return v.toFixed(0);
}
